// noinspection JSUnusedLocalSymbols

import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { CacheService } from '../../../../../services/cache-service';
import { map, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { VerifyMemberRequest } from '../../../../../models/users/requests/verify-member-request';
import { ToastService } from '../../../../../services/toast-service';
import { ConfirmMemberRequest } from '../../../../../models/users/requests/confirm-member-request';
import { VerifyEmailRequest } from '../../../../../models/users/requests/verify-email-request';
import {
  MemberAuthFlow,
  MemberAuthFlowSubtext,
  MemberAuthFlowTitle
} from '../../../../../models/users/enum/member-auth-flow.enum';
import { BaseAuthViewModel } from '../../../../../models/base/base-auth-view-model';
import { UserDomainModel } from '../../../../../domainModels/user-domain-model';
import { ForgotPasswordRequest } from '../../../../../models/users/requests/forgot-password-request';
import { PortalType } from '../../../../../models/enum/shared/portal-type';
import { HttpStatusCode } from '@angular/common/http';
import { CustomError } from '../../../../../models/shared/custom-error';
import { Routes } from '../../../../../../routes/routes';

@Injectable()
export class MemberAuthViewModel extends BaseAuthViewModel {
  constructor(
    public router: Router,
    public cacheService: CacheService,
    public userDomainModel: UserDomainModel,
    public toastService: ToastService
  ) {
    super(router, toastService, cacheService);
    this.init();
  }

  // Observables
  public nextAuthFlow: Subject<MemberAuthFlow> = new Subject<MemberAuthFlow>();

  private _memberInviteId = new BehaviorSubject<string>('');
  public readonly memberInviteId$ = this._memberInviteId as Observable<string>;

  private _newMemberEmail = new BehaviorSubject<string>('');
  public readonly newMemberEmail$ = this._newMemberEmail as Observable<string>;

  private _verifyMemberFailed = new BehaviorSubject<boolean>(false);
  public readonly verifyMemberFailed$ = this._verifyMemberFailed.asObservable();

  private readonly employerDetailsKey = 'employer-details';

  public authFlowMap = new Map<number, MemberAuthFlow>();

  public readonly authFlowTitle$ = this.authFlow$.pipe(map(flow => MemberAuthFlowTitle(flow)));
  public readonly authFlowSubtext$ = this.authFlow$.pipe(map(flow => MemberAuthFlowSubtext(flow)));

  public showBackButton$ = this.authFlow$.pipe(
    map(flow => {
      return ![MemberAuthFlow.SignIn, MemberAuthFlow.MemberWelcome, MemberAuthFlow.MemberAccessRestricted].includes(
        flow
      );
    })
  );

  init() {
    super.init();
    this.generateAuthFlowMap();
  }

  // Auth Flows

  goBack(): void {
    this.authFlow$.once(flow => {
      switch (flow) {
        case MemberAuthFlow.ResetPassword: {
          this.router.navigate(['/auth/forgot-password']).then();
          break;
        }
        case MemberAuthFlow.ForgotPassword: {
          this.router.navigate(['/auth/sign-in']).then();
          break;
        }
        case MemberAuthFlow.MemberWelcome: {
          this.router.navigate(['/auth/sign-in']).then();
          break;
        }
        case MemberAuthFlow.MemberSetup: {
          this.router.navigate(['/auth/member-welcome']).then();
          break;
        }
        case MemberAuthFlow.MemberSendEmailVerification: {
          this.router.navigate(['/auth/member-setup']).then();
          break;
        }
        case MemberAuthFlow.MemberEnterEmailVerification: {
          this.router.navigate(['/auth/member-send-email-verification']).then();
          break;
        }
        case MemberAuthFlow.MemberMFAVerification: {
          this.router.navigate(['/auth/sign-in']).then();
          break;
        }
      }
    });
  }

  public generateAuthFlowMap() {
    this.authFlowMap?.set(0, MemberAuthFlow.SignIn);
    this.authFlowMap?.set(1, MemberAuthFlow.ForgotPassword);
    this.authFlowMap?.set(2, MemberAuthFlow.ResetPassword);
    this.authFlowMap?.set(3, MemberAuthFlow.MemberWelcome);
    this.authFlowMap?.set(4, MemberAuthFlow.MemberSetup);
    this.authFlowMap?.set(5, MemberAuthFlow.MemberSendEmailVerification);
    this.authFlowMap?.set(6, MemberAuthFlow.MemberEnterEmailVerification);
  }

  public setNewMemberEmail(email: string) {
    this._newMemberEmail.next(email);
  }

  public clearVerifyMemberFailed(): void {
    this._verifyMemberFailed.next(false);
  }

  private listenToQueryParams = this.route.queryParams.subscribeWhileAlive({
    owner: this,
    next: params => {
      const memberInviteId = decodeURIComponent(decodeURIComponent(params?.inviteId || params?.InviteId || ''));
      if (!!memberInviteId) {
        this._memberInviteId.next(memberInviteId);
        this.router?.navigate([], { queryParams: {}, replaceUrl: true, relativeTo: this.route }).then();
      }
    }
  });

  // Auth Requests

  public verifyMember(req: VerifyMemberRequest): void {
    const loadingMessage = $localize`Verifying`;
    const noInviteIdError = $localize`No Invite ID`;
    this._verifyMemberFailed.next(false);
    this.memberInviteId$
      .pipe(
        switchMap(inviteId => {
          if (!!inviteId) {
            this._loadingOpts.addRequest(loadingMessage);
            return this.userAPI.verifyMember(req, inviteId);
          }
          return throwError(() => new Error(noInviteIdError));
        }),
        take(1)
      )
      .subscribe({
        next: res => {
          this._loadingOpts.removeRequest(loadingMessage);
          this.toastService.publishSuccessMessage($localize`Success!`, $localize`Account Verified!`);
          this.router.navigate(['/auth/member-setup']).then();
        },
        error: (error: CustomError) => {
          if (error?.code === HttpStatusCode.Locked) {
            this.router.navigate([Routes.accessRestricted()], { replaceUrl: true }).then(() => {});
          } else {
            this._verifyMemberFailed.next(true);
            this.toastService.publishErrorMessage($localize`Could not connect to your account.`);
          }
          this._loadingOpts.removeRequest(loadingMessage);
        }
      });
  }

  public confirmMember(req: ConfirmMemberRequest): void {
    const loadingMessage = $localize`Confirming`;
    const noInviteIdError = $localize`No Invite ID`;
    this.memberInviteId$
      .pipe(
        switchMap(inviteId => {
          if (!!inviteId) {
            this._loadingOpts.addRequest(loadingMessage);
            return this.userAPI.confirmMember(req, inviteId);
          }
          return throwError(() => new Error(noInviteIdError));
        }),
        take(1)
      )
      .subscribe({
        next: _ => {
          this._loadingOpts.removeRequest(loadingMessage);
          this.toastService.publishSuccessMessage($localize`Success!`, $localize`Account Created!`);
          this._newMemberEmail.next(req.email);
          this.router.navigate(['/auth/member-send-email-verification']).then();
        },
        error: (error: CustomError) => {
          if (error?.code === HttpStatusCode.Locked) {
            this.router.navigate([Routes.accessRestricted()], { replaceUrl: true }).then(() => {});
          } else {
            this.toastService.publishError(error);
          }
          this._loadingOpts.removeRequest(loadingMessage);
        }
      });
  }

  public verifyMemberEmail(req: VerifyEmailRequest): void {
    const loadingMessage = $localize`Confirming Email`;
    this._loadingOpts.addRequest(loadingMessage);
    this.userAPI.verifyMemberEmail(req).subscribe({
      next: res => {
        this.toastService.publishSuccessMessage($localize`Success!`, $localize`Email Verified!`);
        this.router.navigate(['/auth/sign-in'], { queryParams: { email: req.email } }).then();
        this.cacheService.clearAllCaches();
        this._loadingOpts.removeRequest(loadingMessage);
      },
      error: (error: CustomError) => {
        if (error?.code === HttpStatusCode.Locked) {
          this.router.navigate([Routes.accessRestricted()], { replaceUrl: true }).then(() => {});
        } else {
          this.toastService.publishError(error);
        }
        this._loadingOpts.removeRequest(loadingMessage);
      }
    });
  }

  public resendEmail(): void {
    const loadingMessage = $localize`Sending Email`;
    this._loadingOpts.addRequest(loadingMessage);
    this.newMemberEmail$
      .pipe(
        take(1),
        switchMap(email => this.userDomainModel.forgotPassword(new ForgotPasswordRequest(email), PortalType.Member))
      )
      .subscribe({
        next: res => {
          this.toastService.publishSuccessMessage($localize`Success!`, $localize`Email Sent`);
          this._loadingOpts.removeRequest(loadingMessage);
        },
        error: (error: CustomError) => {
          if (error?.code === HttpStatusCode.Locked) {
            this.router.navigate([Routes.accessRestricted()], { replaceUrl: true }).then(() => {});
          } else {
            this.toastService.publishError(error);
          }
          this._loadingOpts.removeRequest(loadingMessage);
        }
      });
  }
}
